// src/pages/Login.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import styles from '../components/auth/styles.module.css';
import BouteeLogo from '../components/auth/Boutee Logo White.png';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  
  const { signIn, resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await signIn(email, password);
      navigate('/');
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Failed to sign in. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!resetEmail) {
      setError('Please enter your email address');
      return;
    }

    setIsLoading(true);
    setError('');
    setResetMessage('');

    try {
      await resetPassword(resetEmail);
      setResetMessage('Password reset email sent! Please check your inbox.');
      setResetEmail('');
      setTimeout(() => {
        setShowResetPassword(false);
        setResetMessage('');
      }, 5000);
    } catch (err) {
      console.error('Reset password error:', err);
      setError('Failed to send reset email. Please check the email address.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.loginContainer}>
      <div className={styles.loginCard}>
        {/* Header Section */}
        <div className={styles.cardHeader}>
          <img 
            src={BouteeLogo} 
            alt="Boutee Logo" 
            className={styles.logo}
          />
          <p className={styles.logoSubtitle}>Jeweller Portal</p>
        </div>
        
        {/* Form Section */}
        {!showResetPassword ? (
          <form onSubmit={handleSubmit} className={styles.loginForm}>
            <div className={styles.cardBody}>
              {/* Email Field */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="email">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={styles.formInput}
                  disabled={isLoading}
                />
              </div>
              
              {/* Password Field */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="password">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className={styles.formInput}
                  disabled={isLoading}
                />
              </div>
              
              {/* Error Message */}
              {error && (
                <div className={styles.errorMessage}>
                  <svg 
                    className={styles.errorIcon} 
                    viewBox="0 0 20 20" 
                    fill="currentColor"
                  >
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              {/* Success Message */}
              {resetMessage && (
                <div className={styles.successMessage}>
                  <svg 
                    className={styles.successIcon} 
                    viewBox="0 0 20 20" 
                    fill="currentColor"
                  >
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{resetMessage}</span>
                </div>
              )}
            </div>
            
            {/* Submit Button */}
            <div className={styles.cardFooter}>
              <button 
                type="submit" 
                disabled={isLoading}
                className={styles.submitButton}
              >
                {isLoading ? (
                  <>
                    <span className={styles.loadingSpinner}></span>
                    Signing In...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
              
              {/* Forgot Password Link */}
              <div className={styles.supportText}>
                <button
                  type="button"
                  onClick={() => {
                    setShowResetPassword(true);
                    setError('');
                    setResetMessage('');
                  }}
                  className={styles.supportLink}
                  style={{ background: 'none', border: 'none', padding: 0 }}
                >
                  Forgot Password?
                </button>
              </div>

              {/* Admin Contact */}
              <div className={styles.supportText} style={{ marginTop: '8px' }}>
                Don't have an account?{" "}
                <a 
                  href="mailto:ethan@boutee.co.uk?subject=RE%3A%20I%20need%20an%20account&body=Hi%20Boutee%2C%0A%0AI%20need%20access%20to%20a%20jeweller%20portal..."
                  className={styles.supportLink}
                >
                  Contact Admin
                </a>
              </div>
            </div>
          </form>
        ) : (
          <form onSubmit={handleResetPassword} className={styles.loginForm}>
            <div className={styles.cardBody}>
              {/* Instructions */}
              <p className={styles.resetInstructions}>
                Enter your email address and we'll send you a link to reset your password.
              </p>

              {/* Email Field */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="resetEmail">
                  Email
                </label>
                <input
                  id="resetEmail"
                  type="email"
                  placeholder="name@example.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  className={styles.formInput}
                  disabled={isLoading}
                />
              </div>
              
              {/* Error Message */}
              {error && (
                <div className={styles.errorMessage}>
                  <svg 
                    className={styles.errorIcon} 
                    viewBox="0 0 20 20" 
                    fill="currentColor"
                  >
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              {/* Success Message */}
              {resetMessage && (
                <div className={styles.successMessage}>
                  <svg 
                    className={styles.successIcon} 
                    viewBox="0 0 20 20" 
                    fill="currentColor"
                  >
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{resetMessage}</span>
                </div>
              )}
            </div>
            
            {/* Submit Button */}
            <div className={styles.cardFooter}>
              <button 
                type="submit" 
                disabled={isLoading}
                className={styles.submitButton}
              >
                {isLoading ? (
                  <>
                    <span className={styles.loadingSpinner}></span>
                    Sending...
                  </>
                ) : (
                  'Send Reset Email'
                )}
              </button>
              
              {/* Back to Login */}
              <div className={styles.supportText}>
                <button
                  type="button"
                  onClick={() => {
                    setShowResetPassword(false);
                    setError('');
                    setResetMessage('');
                  }}
                  className={styles.supportLink}
                  style={{ background: 'none', border: 'none', padding: 0 }}
                >
                  Back to Login
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;